import { useState, useEffect, useRef, useCallback } from 'react'
import {
  fetchMessages,
  sendMessage as sendMessageApi,
  deleteMessage as deleteMessageApi,
  type CommunityMessage,
} from '@/services/communityService'

const POLL_INTERVAL = 5000
const MAX_MESSAGES = 200
const INITIAL_LIMIT = 50
const HISTORY_LIMIT = 50

export function useCommunityChat(isOpen: boolean) {
  const [messages, setMessages] = useState<CommunityMessage[]>([])
  const [sending, setLoading] = useState(false)
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [toastMessage, setToastMessage] = useState<CommunityMessage | null>(null)

  const lastSeenIdRef = useRef<string | null>(null)
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const isPollingRef = useRef(false)
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ─── 初始加载 ────────────────────────────────────────

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const data = await fetchMessages(undefined, INITIAL_LIMIT)
        if (cancelled) return
        setMessages(data)
        if (data.length > 0) {
          lastSeenIdRef.current = data[data.length - 1]._id
        }
      } catch {
        // 静默失败，下次轮询会重试
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  // ─── 轮询逻辑 ───────────────────────────────────────

  const poll = useCallback(async () => {
    if (isPollingRef.current) return
    isPollingRef.current = true
    try {
      const after = lastSeenIdRef.current || undefined
      const newMessages = await fetchMessages(after, 50)
      if (newMessages.length === 0) return

      setMessages((prev) => {
        const existingIds = new Set(prev.map((m) => m._id))
        const fresh = newMessages.filter((m) => !existingIds.has(m._id))
        if (fresh.length === 0) return prev

        const merged = [...prev, ...fresh].slice(-MAX_MESSAGES)
        return merged
      })

      lastSeenIdRef.current = newMessages[newMessages.length - 1]._id

      // 面板关闭时显示 toast
      if (!isOpen) {
        const firstNew = newMessages[0]
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
        setToastMessage(firstNew)
        toastTimerRef.current = setTimeout(() => {
          setToastMessage(null)
        }, 2000)
      }
    } catch {
      // 静默，下个周期重试
    } finally {
      isPollingRef.current = false
    }
  }, [isOpen])

  // 定时轮询
  useEffect(() => {
    pollTimerRef.current = setInterval(poll, POLL_INTERVAL)
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current)
    }
  }, [poll])

  // 页面可见性：隐藏时暂停，恢复时立即拉一次
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden) {
        poll()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [poll])

  // ─── 发送消息 ────────────────────────────────────────

  const sendMessage = useCallback(
    async (content: string, mentions: string[]) => {
      // 乐观更新
      const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
      const optimistic: CommunityMessage = {
        _id: tempId,
        userId: '',
        username: '',
        avatar: '',
        content,
        mentions,
        createdAt: new Date().toISOString(),
      }

      setMessages((prev) => [...prev, optimistic])
      setLoading(true)

      try {
        const real = await sendMessageApi(content, mentions)
        setMessages((prev) =>
          prev.map((m) => (m._id === tempId ? real : m)),
        )
        lastSeenIdRef.current = real._id
      } catch {
        // 标记发送失败：移除乐观消息
        setMessages((prev) => prev.filter((m) => m._id !== tempId))
        throw new Error('发送失败')
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  // ─── 删除消息 ────────────────────────────────────────

  const deleteMessage = useCallback(
    async (messageId: string) => {
      try {
        await deleteMessageApi(messageId)
        setMessages((prev) => prev.filter((m) => m._id !== messageId))
      } catch {
        throw new Error('删除失败')
      }
    },
    [],
  )

  // ─── 加载历史消息 ────────────────────────────────────

  const loadHistory = useCallback(async () => {
    if (loadingHistory || messages.length === 0) return
    setLoadingHistory(true)
    try {
      // 拉取比最早消息更早的消息：取全部最新 N 条然后过滤
      const allRecent = await fetchMessages(undefined, messages.length + HISTORY_LIMIT)
      const existingIds = new Set(messages.map((m) => m._id))
      const older = allRecent.filter((m) => !existingIds.has(m._id))
      if (older.length > 0) {
        setMessages((prev) => [...older, ...prev].slice(-MAX_MESSAGES))
      }
    } catch {
      // 静默
    } finally {
      setLoadingHistory(false)
    }
  }, [loadingHistory, messages])

  // ─── 清理 ────────────────────────────────────────────

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
    }
  }, [])

  return {
    messages,
    sending,
    loadingHistory,
    toastMessage,
    sendMessage,
    deleteMessage,
    loadHistory,
  }
}
